// Netz weg — und jetzt?
//
// Der Fahrer steht in einer Hofeinfahrt am Ortsrand von Gütersloh. Ein Balken,
// manchmal keiner. Was dann passiert, entscheidet, ob die App eine App ist:
//
//   • Eine WEBSEITE zeigt den Dino und ist weg.
//   • Eine APP startet trotzdem (Service Worker, siehe vite.config.ts), sagt
//     ehrlich "kein Netz" und macht weiter, sobald wieder eins da ist.
//
// Was wir bewusst NICHT tun: Schreibvorgänge offline in eine Warteschlange
// legen. Eine Unterschrift, die erst in zwanzig Minuten in der Datenbank landet,
// wäre schlimmer als eine, die sofort fehlschlägt — der Fahrer würde weiterfahren
// und glauben, es sei alles gebucht. Er merkt jetzt sofort, dass er kein Netz
// hat, und wartet die zehn Sekunden ab.

import { useEffect, useSyncExternalStore } from "react";
import { useQueryClient } from "@tanstack/react-query";

function abonnieren(melden: () => void) {
  window.addEventListener("online", melden);
  window.addEventListener("offline", melden);
  return () => {
    window.removeEventListener("online", melden);
    window.removeEventListener("offline", melden);
  };
}

/** true, sobald der Browser meldet, dass kein Netz da ist. */
export function useOffline(): boolean {
  return useSyncExternalStore(
    abonnieren,
    () => !navigator.onLine,
    () => false // Beim Server-Render gibt es kein navigator — nie "offline" annehmen.
  );
}

/**
 * Holt alles nach, sobald das Netz zurückkommt.
 *
 * `refetchOnReconnect` in main.tsx greift nur bei Abfragen, die gerade jemand
 * ansieht. Der Fahrer hatte die App aber die ganze Zeit offen — die Tour-Abfrage
 * ist "aktiv", aber ihre letzten Versuche sind alle ins Leere gelaufen. Deshalb
 * hier zusätzlich einmal hart alles ungültig machen.
 */
export function useNachNetzAktualisieren() {
  const qc = useQueryClient();
  useEffect(() => {
    const wiederDa = () => void qc.invalidateQueries();
    window.addEventListener("online", wiederDa);
    return () => window.removeEventListener("online", wiederDa);
  }, [qc]);
}
